import { JobViewModel } from "../ViewModels/JobViewModel";
import { JobItemView } from "./JobItemView";
import { StreamContextMenuView } from "./StreamContextMenuView";
import { NLIJobService } from "../../../../services/NLIJobService";
import { Select, Tag, notification } from "antd";
import { PlusCircleFilled, SearchOutlined, DownOutlined, RightOutlined, CheckOutlined } from "@ant-design/icons";
import { useState, useRef, useEffect, useCallback } from "react";
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { useDarkMode, useThemeColors } from "redux/hooks";
import { ConditionalTooltip } from "./ConditionalTooltip";
import QueryStreamLogo from "../../../icons/QueryStreamLogo";

export function JobListView(props: {
    jobs: readonly JobViewModel[];
    tagList: string[];
    anyContextMenuOpen: boolean;
    setAnyContextMenuOpen: (open: boolean) => void;
    onNewStream: () => void;
    modelAlias: string;
    jobService: NLIJobService;
    onToggleCollapse: () => void;
    isCollapsed?: boolean;
}) {
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const jobList = useRef<HTMLDivElement>(null);

    const [searchValues, setSearchValues] = useState<string[]>([]);
    const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
    const [streamContextMenu, setStreamContextMenu] = useState<{ visible: boolean, x: number, y: number, jobID: string | null }>({ visible: false, x: 0, y: 0, jobID: null });
    const streamContextMenuRef = useRef<HTMLDivElement>(null);
    const [renamingStream, setRenamingStream] = useState<{ jobID: string, alias: string } | null>(null);
    const renameInputRef = useRef<HTMLInputElement>(null);
    const renameFormRef = useRef<HTMLDivElement>(null);
    const hasSelectedRef = useRef<string | null>(null);

    // Auto-expand newly selected jobs
    useEffect(() => {
        const selectedJob = props.jobs.find(job => job.isSelected && !job.replyTarget);
        if (selectedJob && !expandedJobs.has(selectedJob.jobID)) {
            setExpandedJobs(prev => new Set(prev).add(selectedJob.jobID));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.jobs]);

    const tagRender = (tagProps: CustomTagProps) => {
        const { label, value, closable, onClose } = tagProps;
        const isTag = typeof value === 'string' && props.tagList.includes(value);

        return (
            <Tag
                closable={closable}
                onClose={onClose}
                style={{
                    marginRight: 3,
                    backgroundColor: isTag ? (darkMode ? themeColors.backgroundColorTertiary : '#E6F0F7') : (darkMode ? themeColors.borderColor : undefined),
                    borderColor: isTag ? themeColors.primaryColorLight : (darkMode ? themeColors.borderColorSecondary : undefined),
                    color: darkMode ? themeColors.textColor : undefined,
                }}
            >
                {label}
            </Tag>
        );
    };

    // Build conversation threads as a tree structure
    const buildThreads = () => {
        const childrenMap = new Map<string, JobViewModel[]>();
        const originalJobs: JobViewModel[] = [];

        // First pass: identify original jobs
        props.jobs.forEach(job => {
            if (!job.replyTarget) {
                originalJobs.push(job);
            }
        });

        // Second pass: build parent-child relationships recursively
        const getJobIDFromReplyTarget = (replyTarget: string): string => {
            return replyTarget.split('-').slice(1).join('-');
        };

        props.jobs.forEach(job => {
            if (job.replyTarget) {
                const parentJobID = getJobIDFromReplyTarget(job.replyTarget);
                if (!childrenMap.has(parentJobID)) {
                    childrenMap.set(parentJobID, []);
                }
                childrenMap.get(parentJobID)!.push(job);
            }
        });

        // Sort children by start time for each parent
        childrenMap.forEach((children) => {
            children.sort((a, b) => a.startTime - b.startTime);
        });

        return { originalJobs, childrenMap };
    };

    const { originalJobs, childrenMap } = buildThreads();

    const toggleExpand = (jobID: string) => {
        const newExpanded = new Set(expandedJobs);
        if (newExpanded.has(jobID)) {
            newExpanded.delete(jobID);
        } else {
            newExpanded.add(jobID);
        }
        setExpandedJobs(newExpanded);
    };

    // Recursive function to flatten all descendants of a job
    const getAllDescendants = (job: JobViewModel): JobViewModel[] => {
        const children = childrenMap.get(job.jobID) || [];
        const descendants: JobViewModel[] = [];
        children.forEach(child => {
            descendants.push(child);
            descendants.push(...getAllDescendants(child));
        });
        return descendants;
    };

    // Recursive function to check if any descendant matches search
    const hasMatchingDescendant = (job: JobViewModel, searchValues: string[], tagList: string[]): boolean => {
        const allDescendants = getAllDescendants(job);
        return allDescendants.some(descendant => {
            return searchValues.every(value => {
                if (tagList.includes(value)) {
                    return descendant.tags && descendant.tags.includes(value);
                }
                return descendant.question.toLowerCase().includes(value.toLowerCase());
            });
        });
    };

    const handleStreamRightClick = useCallback((event: React.MouseEvent, jobID: string) => {
        event.preventDefault();
        event.stopPropagation();
        setStreamContextMenu({ visible: true, x: event.clientX, y: event.clientY, jobID });
        props.setAnyContextMenuOpen(true);
    }, [props]);


    useEffect(() => {
        if (renamingStream && renameInputRef.current) {
            const input = renameInputRef.current;
            // Only select on initial open, not when user is typing
            if (hasSelectedRef.current !== renamingStream.jobID) {
                input.focus();
                setTimeout(() => {
                    input.select();
                    // Scroll to the beginning (left side) after selection
                    input.setSelectionRange(0, input.value.length);
                    input.scrollLeft = 0;
                }, 0);
                hasSelectedRef.current = renamingStream.jobID;
            }
        } else {
            hasSelectedRef.current = null;
        }
    }, [renamingStream]);

    const handleRenameStream = useCallback((jobID: string) => {
        const job = props.jobs.find(j => j.jobID === jobID && !j.replyTarget);
        if (job) {
            setRenamingStream({ jobID, alias: job.alias });
        }
    }, [props.jobs]);

    const handleRenameSubmit = useCallback(async () => {
        if (!renamingStream) return;

        const newAlias = renamingStream.alias.trim();

        // Check if alias is empty or just whitespace
        if (!newAlias || newAlias.length === 0) {
            notification.error({
                message: 'Invalid stream name',
                description: 'Stream name cannot be empty.',
                placement: 'bottomLeft',
                duration: 3
            });
            setRenamingStream(null);
            return;
        }

        // Validate: only alphanumeric and specific special characters (including spaces)
        const allowedPattern = /^[a-zA-Z0-9_\-:,.%$&*^<>!?"()[\]{}\\/|`~@#+= ]+$/;
        if (!allowedPattern.test(newAlias)) {
            // Find invalid characters
            const invalidChars = new Set<string>();
            for (const char of newAlias) {
                if (!/[a-zA-Z0-9_\-:,.%$&*^<>!?"()[\]{}\\/|`~@#+= ]/.test(char)) {
                    invalidChars.add(char === ' ' ? '(space)' : char);
                }
            }
            const invalidCharsList = Array.from(invalidChars).join(', ');

            // Invalid characters - show error notification
            notification.error({
                message: 'Invalid stream name',
                description: `Invalid characters: ${invalidCharsList}`,
                placement: 'bottomLeft',
                duration: 3
            });
            setRenamingStream(null);
            return;
        }

        try {
            await props.jobService.updateJobAlias(renamingStream.jobID, newAlias);
            // Update the job's alias in the view model
            const job = props.jobs.find(j => j.jobID === renamingStream.jobID && !j.replyTarget);
            if (job) {
                job.alias = newAlias;
            }
            notification.success({
                message: 'Stream renamed',
                description: `"${newAlias}"`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            console.error("Failed to update job alias:", error);
            notification.error({
                message: 'Failed to rename stream',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }

        setRenamingStream(null);
    }, [renamingStream, props.jobService, props.jobs]);

    const handleRenameCancel = useCallback(() => {
        setRenamingStream(null);
    }, []);

    const handleRenameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleRenameSubmit();
        } else if (e.key === 'Escape') {
            handleRenameCancel();
        }
    }, [handleRenameSubmit, handleRenameCancel]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (streamContextMenuRef.current && !streamContextMenuRef.current.contains(event.target as Node)) {
                setStreamContextMenu({ visible: false, x: 0, y: 0, jobID: null });
                props.setAnyContextMenuOpen(false);
            }
            if (renamingStream && renameFormRef.current && !renameFormRef.current.contains(event.target as Node)) {
                handleRenameCancel();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [props, renamingStream, handleRenameCancel]);

    return (
        <div className="flex-1 flex-column" style={{ padding: "0.5em", paddingRight: "0.2em", position: "relative", background: themeColors.backgroundColorSecondary, height: "100%" }}>
            <style>{`
                .job-list-scroll::-webkit-scrollbar {
                    width: 10px;
                }
                .job-list-scroll::-webkit-scrollbar-track {
                    background: ${themeColors.backgroundColorSecondary};
                }
                .job-list-scroll::-webkit-scrollbar-thumb {
                    background: ${darkMode ? themeColors.borderColorSecondary : "#b0b0b0"};
                    border-radius: 5px;
                }
                .job-list-scroll::-webkit-scrollbar-thumb:hover {
                    background: ${themeColors.borderColor};
                }
                .job-list-select-dropdown .ant-select-item {
                    padding: 1px 6px !important;
                }
                ${darkMode ? `
                    .job-list-select-dropdown {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                        border-color: ${themeColors.borderColor} !important;
                        box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.48) !important;
                        outline: none !important;
                    }
                    .job-list-select-dropdown,
                    .job-list-select-dropdown > * {
                        border-color: ${themeColors.borderColor} !important;
                    }
                    .job-list-select-dropdown .ant-select-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select-dropdown .ant-select-item:hover {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .job-list-select-dropdown .ant-select-item-option-selected {
                        background-color: ${themeColors.selectedBackground} !important;
                    }
                    .job-list-select-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
                        background-color: ${themeColors.selectedBackground} !important;
                    }
                    .job-list-select .ant-select-selector {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border-color: ${themeColors.borderColor} !important;
                    }
                    .job-list-select .ant-select-selection-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select .ant-select-selection-placeholder {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .job-list-select .ant-select-arrow {
                        display: none !important;
                    }
                    .job-list-select .ant-select-clear {
                        color: ${themeColors.textColor} !important;
                        background-color: transparent !important;
                    }
                    .job-list-select .ant-select-clear:hover {
                        color: ${themeColors.textColorSecondary} !important;
                    }
                    .job-list-select .ant-select-selection-search-input {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select .ant-select-selection-item-remove {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select .ant-select-selection-item-remove:hover {
                        color: ${themeColors.textColorSecondary} !important;
                    }
                    .job-list-select .ant-tag-close-icon {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select .ant-tag-close-icon:hover {
                        color: ${themeColors.textColorSecondary} !important;
                    }
                    .job-list-select-dropdown .ant-select-item-empty {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select-dropdown .ant-empty {
                        color: ${themeColors.textColor} !important;
                    }
                    .job-list-select-dropdown .ant-empty-description {
                        color: ${themeColors.textColorMuted} !important;
                    }
                ` : `
                    .job-list-select .ant-select-arrow {
                        display: none !important;
                    }
                `}
            `}</style>
            <div style={{ marginBottom: "0.5em", paddingRight: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5em" }}>
                    <ConditionalTooltip title={props.isCollapsed ? "Expand panel" : "Collapse panel"}>
                        <div onClick={props.onToggleCollapse} style={{ cursor: "pointer" }}>
                    <QueryStreamLogo color={themeColors.primaryColor} />
                        </div>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="New Stream">
                        <button
                            onClick={props.onNewStream}
                            style={{
                                border: "none",
                                backgroundColor: "transparent",
                                color: themeColors.primaryColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                fontSize: "34px",
                                padding: "0",
                                marginTop: "0.075em"
                            }}
                        >
                            <PlusCircleFilled />
                        </button>
                    </ConditionalTooltip>
                </div>
                <ConditionalTooltip title="Filter threads by free text or add tags to narrow results">
                    <Select
                        mode="tags"
                        style={{ width: "100%" }}
                        placeholder="Search by text and/or tags..."
                        maxTagCount='responsive'
                        value={searchValues}
                        size="large"
                        onChange={setSearchValues}
                        allowClear={true}
                        disabled={props.isCollapsed}
                        tagRender={tagRender}
                        popupClassName="job-list-select-dropdown"
                        className="job-list-select"
                    >
                        {props.tagList.map(tag => (
                            <Select.Option key={tag} value={tag}>{tag}</Select.Option>
                        ))}
                    </Select>
                </ConditionalTooltip>
            </div>
            <div ref={jobList} className="flex-1 scroll job-list-scroll" style={{ overflow: "auto", scrollbarGutter: "stable" }}>
                {
                    originalJobs
                        .map(originalJob => {
                            const allDescendants = getAllDescendants(originalJob);
                            const allJobsInThread = [originalJob, ...allDescendants];

                            // Check if parent (alias) matches ALL text search criteria (tags excluded)
                            const textSearchValues = searchValues.filter(value => !props.tagList.includes(value));
                            const tagSearchValues = searchValues.filter(value => props.tagList.includes(value));
                            const hasTagFilter = tagSearchValues.length > 0;

                            const parentMatches = searchValues.length === 0 || (
                                textSearchValues.length > 0 &&
                                textSearchValues.every(value => originalJob.alias.toLowerCase().includes(value.toLowerCase()))
                            );

                            // Check if the original job itself matches any of the tag search values
                            const parentTagMatches = hasTagFilter && originalJob.tags && tagSearchValues.every(value => originalJob.tags!.includes(value));

                            // Filter based on search criteria - check if parent matches or any descendant matches
                            const hasMatchingDesc = searchValues.length === 0 || hasMatchingDescendant(originalJob, searchValues, props.tagList);

                            // If tag filtering is present, streams (parents) should only show if they have matching descendants
                            // since streams don't have tags themselves
                            const shouldShow = searchValues.length === 0 || (
                                hasTagFilter
                                    ? (hasMatchingDesc || parentTagMatches)  // With tags: show if parent's tags match or descendants match
                                    : (parentMatches || hasMatchingDesc)  // Without tags: show if parent (alias) or descendants match
                            );

                            // Only show parent if it or any descendant passes the filter
                            if (!shouldShow) return null;

                            let isExpanded = expandedJobs.has(originalJob.jobID);
                            // If any job in the thread matches the search criteria (including parent tags), expand it.
                            if ((hasMatchingDesc || parentTagMatches) && !isExpanded && searchValues.length > 0) {
                                isExpanded = true;
                            }
                            // Check if any job in this thread is selected
                            const isThreadSelected = allJobsInThread.some(job => job.isSelected);

                            return (
                                <div
                                    key={originalJob.jobID}
                                    style={{
                                        backgroundColor: isThreadSelected ? (darkMode ? themeColors.selectedBackground || themeColors.primaryColor + "33" : "white") : "transparent",
                                        border: isThreadSelected ? `1px solid ${darkMode ? themeColors.primaryColor : themeColors.borderColor}` : "1px solid transparent",
                                        borderRadius: isThreadSelected ? "5px" : "0",
                                        marginBottom: "2px"
                                    }}
                                >
                                    {/* Parent Row - Job Alias */}
                                    {renamingStream && renamingStream.jobID === originalJob.jobID ? (
                                        <div
                                            ref={renameFormRef}
                                            style={{
                                                padding: "0.3em 0.5em",
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <span style={{ marginRight: "0.5em", fontSize: "0.7em", color: themeColors.textColor }}>
                                                {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                            </span>
                                            <input
                                                ref={renameInputRef}
                                                type="text"
                                                value={renamingStream.alias}
                                                onChange={(e) => setRenamingStream({ ...renamingStream, alias: e.target.value })}
                                                onKeyDown={handleRenameKeyDown}
                                                style={{
                                                    flex: 1,
                                                    fontSize: "0.95em",
                                                    padding: "0",
                                                    border: `1px solid ${themeColors.primaryColor}`,
                                                    backgroundColor: themeColors.backgroundColor,
                                                    color: themeColors.textColor,
                                                    outline: "none",
                                                    margin: 0,
                                                    marginLeft: "0.04em",
                                                    lineHeight: "1.52",
                                                    height: "1.52em"
                                                }}
                                            />
                                            <ConditionalTooltip title="Submit">
                                                <button
                                                    onClick={handleRenameSubmit}
                                                    style={{
                                                        fontSize: "0.85em",
                                                        backgroundColor: themeColors.primaryColor,
                                                        color: "white",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        height: "1.7em",
                                                        width: "1.7em",
                                                        border: "none",
                                                        alignSelf: "flex-start",
                                                        flexShrink: 0,
                                                        marginRight: "-0.55em"
                                                    }}
                                                >
                                                    <CheckOutlined />
                                                </button>
                                            </ConditionalTooltip>
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                padding: "0.3em 0.5em",
                                                display: "flex",
                                                alignItems: "center",
                                                cursor: "pointer",
                                                userSelect: "none"
                                            }}
                                            onClick={() => toggleExpand(originalJob.jobID)}
                                            onContextMenu={(e) => handleStreamRightClick(e, originalJob.jobID)}
                                        >
                                            <ConditionalTooltip title={isExpanded ? "Collapse thread" : "Expand thread"}>
                                                <span style={{ marginRight: "0.64em", fontSize: "0.7em", color: themeColors.textColor }}>
                                                    {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                                </span>
                                            </ConditionalTooltip>
                                            <ConditionalTooltip title={originalJob.alias?.trim() || ''}>
                                                <span
                                                    style={{ fontSize: "0.95em", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: themeColors.textColor }}
                                                >
                                                    {originalJob.alias}
                                                </span>
                                            </ConditionalTooltip>
                                        </div>
                                    )}

                                    {/* Sub-Rows - Parent request first, then all descendants in flat list */}
                                    {isExpanded && (() => {
                                        const allDescendants = getAllDescendants(originalJob);
                                        const allJobsInThread = [originalJob, ...allDescendants];

                                        // Filter based on search
                                        const filteredJobs = searchValues.length === 0
                                            ? allJobsInThread
                                            : allJobsInThread.filter(job => {
                                                return searchValues.every(value => {
                                                    if (props.tagList.includes(value)) {
                                                        return job.tags && job.tags.includes(value);
                                                    }
                                                    return job.question.toLowerCase().includes(value.toLowerCase());
                                                });
                                            });

                                        return filteredJobs.map(job => (
                                            <div key={job.jobID}>
                                                <JobItemView
                                                    model={job}
                                                    anyContextMenuOpen={props.anyContextMenuOpen}
                                                    setAnyContextMenuOpen={props.setAnyContextMenuOpen}
                                                    indent={true}
                                                    tagList={props.tagList}
                                                />
                                            </div>
                                        ));
                                    })()}
                                </div>
                            );
                        })
                        .filter(element => element !== null)
                }
            </div>
            {streamContextMenu.visible && streamContextMenu.jobID && (() => {
                const streamJob = originalJobs.find(job => job.jobID === streamContextMenu.jobID);
                return streamJob ? (
                    <StreamContextMenuView
                        model={streamJob}
                        contextMenu={{ visible: streamContextMenu.visible, x: streamContextMenu.x, y: streamContextMenu.y }}
                        setContextMenu={(menu) => setStreamContextMenu(prev => ({ ...prev, ...menu }))}
                        contextMenuRef={streamContextMenuRef}
                        onRename={() => handleRenameStream(streamContextMenu.jobID!)}
                    />
                ) : null;
            })()}
        </div>
    );
}
